import { useEffect, useRef, useState } from 'react';
import type { ToSendItem } from '../types';

function Modal({ label, onCancel, children }: { label: string; onCancel: () => void; children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('button, input, textarea, [tabindex]')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); };
  }, [onCancel]);
  return (
    <div className="pa-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div ref={ref} className="pa-dialog" role="dialog" aria-modal="true" aria-label={label}>
        {children}
      </div>
    </div>
  );
}

// ---- Don't send (§6) ----
export function DontSendDialog({
  item,
  isOwner,
  onCancel,
  onConfirm,
}: {
  item: ToSendItem;
  isOwner: boolean;
  onCancel: () => void;
  onConfirm: (v: { stopPreparing: boolean }) => void;
}) {
  const [reason, setReason] = useState('');
  const [other, setOther] = useState('');
  const [stop, setStop] = useState(false);
  const needsOther = reason === 'Something else';
  const valid = reason && (!needsOther || other.trim());
  // Where the patient can't be billed (Medicaid/CHIP, or a plan or contract that bars it), "will pay for it"
  // would be wrong: the option reads "goes ahead without it" (Oct 22).
  const reasons = [
    `${item.firstName} knows and wants to go ahead`,
    item.billing === 'barred' ? `${item.firstName} goes ahead without it` : `${item.firstName} will pay for it`,
    'The treatment is postponed or cancelled',
    'Something else',
  ];
  return (
    <Modal label={`Don’t send ${item.firstName}’s pre-auth?`} onCancel={onCancel}>
      <h2 className="pa-dialog-title">Don’t send {item.firstName}’s pre-auth?</h2>
      <p className="pa-warnsoft">{item.requirement} {item.why.consequence}</p>
      <div>
        <span className="pa-label">Why not?</span>
        <div className="pa-radio-list" role="radiogroup" aria-label="Why not">
          {reasons.map((r) => (
            <label key={r} className="pa-radio">
              <input type="radio" name="pa-dont-reason" checked={reason === r} onChange={() => { setReason(r); }} />
              <span>{r}</span>
            </label>
          ))}
        </div>
      </div>
      {needsOther ? (
        <input className="pa-field" placeholder="Tell ELVA why" value={other} onChange={(e) => { setOther(e.target.value); }} />
      ) : null}
      {isOwner ? (
        <label className="pa-check">
          <input type="checkbox" checked={stop} onChange={(e) => { setStop(e.target.checked); }} />
          <span>
            Stop preparing these: turn off {item.payer.short} · {item.procedureGroup} in Settings › Pre-auths. Pre-auths already prepared stay.
          </span>
        </label>
      ) : null}
      <div className="pa-dialog-actions">
        <button type="button" className="pa-btn pa-btn-primary" disabled={!valid} onClick={() => { onConfirm({ stopPreparing: stop }); }}>
          Don’t send
        </button>
        <button type="button" className="pa-btn" onClick={onCancel}>Cancel</button>
      </div>
    </Modal>
  );
}
